import { z } from 'zod';
import { UserRole } from '@/models/User';

const AllowedRegistrationRoles = z.enum([UserRole.STUDENT, UserRole.COMPANY]);

const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters')
  .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
  .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
  .regex(/[0-9]/, 'Password must contain at least one number');

export const RegisterDtoSchema = z.object({
  email: z.string().email('Invalid email format'),
  password: passwordSchema,
  confirmPassword: z.string(),
  firstName: z.string().min(2, 'First name must be at least 2 characters'),
  lastName: z.string().min(2, 'Last name must be at least 2 characters'),
  companyName: z.string().optional(),
  role: AllowedRegistrationRoles.optional().default(UserRole.STUDENT),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
}).refine(
  (data) => {
    if (data.role === UserRole.COMPANY) {
      return !!data.companyName && data.companyName.trim().length >= 2;
    }
    return true;
  },
  {
    message: 'Company name must be at least 2 characters',
    path: ['companyName'],
  }
);

export type RegisterDto = z.infer<typeof RegisterDtoSchema>;

export const LoginDtoSchema = z.object({
  email: z.string().email('Invalid email format'),
  password: z.string().min(1, 'Password is required'),
  rememberMe: z.boolean().optional().default(false),
});

export type LoginDto = z.infer<typeof LoginDtoSchema>;

export const ForgotPasswordDtoSchema = z.object({
  email: z.string().email('Invalid email format'),
});

export type ForgotPasswordDto = z.infer<typeof ForgotPasswordDtoSchema>;

export const ResetPasswordDtoSchema = z.object({
  password: passwordSchema,
  confirmPassword: z.string(),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

export type ResetPasswordDto = z.infer<typeof ResetPasswordDtoSchema>;

export const VerifyOtpDtoSchema = z.object({
  email: z.string().email('Invalid email format'),
  otp: z.string().length(6, 'OTP must be 6 digits'),
});

export type VerifyOtpDto = z.infer<typeof VerifyOtpDtoSchema>;

export function getPasswordStrength(password: string): 'weak' | 'medium' | 'strong' {
  const hasUpperCase = /[A-Z]/.test(password);
  const hasLowerCase = /[a-z]/.test(password);
  const hasNumber = /[0-9]/.test(password);
  const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
  const isLongEnough = password.length >= 8;

  if (hasUpperCase && hasLowerCase && hasNumber && hasSpecialChar && isLongEnough) {
    return 'strong';
  } else if (hasUpperCase && hasLowerCase && hasNumber && isLongEnough) {
    return 'medium';
  }
  return 'weak';
}
